import React, { ReactNode } from 'react';
import Badge from '../badge/Badge';

interface TableProps {
  children: ReactNode;
  className?: string;
}

interface TableHeaderProps {
  children: ReactNode;
  className?: string;
}

interface TableBodyProps {
  children: ReactNode;
  className?: string;
}

interface TableCellProps {
  children: ReactNode;
  isHeader?: boolean;
  className?: string;
  colSpan?: number;
  align?: 'left' | 'center' | 'right';
}

interface TableRowProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  interactive?: boolean;
  striped?: boolean;
}

interface TableShellProps {
  children: React.ReactNode;
  className?: string;
  minWidth?: number | string;
  isRefreshing?: boolean;
  refreshLabel?: string;
}

interface TableStateProps {
  children: React.ReactNode;
}

interface TableLoadingStateProps {
  label?: string;
}

interface TableErrorStateProps {
  message: string;
}

export type SortDirection = 'asc' | 'desc';

interface TableSortButtonProps {
  label: string;
  isActive: boolean;
  direction: SortDirection;
  onClick: () => void;
  className?: string;
}

type TableBadgeTone =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'neutral';

interface TableBadgeProps {
  children: React.ReactNode;
  tone?: TableBadgeTone;
}

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ');

const cellAlignment = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const tableHeaderBase =
  'border-b border-[rgb(var(--app-border)/0.58)] bg-[rgb(var(--app-surface-muted)/0.82)]';
const tableBodyBase = 'divide-y divide-[rgb(var(--app-border)/0.3)]';
const tableHeaderCellBase =
  'px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-[rgb(var(--app-muted))]';
const tableBodyCellBase =
  'px-4 py-3 align-middle text-sm text-[rgb(var(--app-muted))]';

const tableBadgeColorMap: Record<
  TableBadgeTone,
  React.ComponentProps<typeof Badge>['color']
> = {
  primary: 'primary',
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
  neutral: 'light',
};

const SortArrow = ({
  active,
  direction,
}: {
  active: boolean;
  direction: SortDirection;
}) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 12 12"
    className={cx(
      'h-3.5 w-3.5 transition-opacity',
      active
        ? 'opacity-100 text-gray-700 dark:text-gray-200'
        : 'opacity-35 text-gray-400 dark:text-gray-500',
    )}
    fill="none"
  >
    <path
      d="M4 4L6 2l2 2"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx(
        !active || direction === 'asc' ? 'opacity-100' : 'opacity-35',
      )}
    />
    <path
      d="M4 8l2 2 2-2"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx(
        !active || direction === 'desc' ? 'opacity-100' : 'opacity-35',
      )}
    />
  </svg>
);

const Table: React.FC<TableProps> = ({ children, className }) => {
  return <table className={cx('min-w-full', className)}>{children}</table>;
};

const TableShell: React.FC<TableShellProps> = ({
  children,
  className,
  minWidth,
  isRefreshing = false,
  refreshLabel = 'Updating...',
}) => {
  const style =
    minWidth == null
      ? undefined
      : { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth };

  return (
    <div
      className={cx(
        'app-panel relative overflow-hidden rounded-[26px]',
        className,
      )}
    >
      {isRefreshing && (
        <div className="pointer-events-none absolute right-4 top-4 z-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-[rgb(var(--app-brand)/0.18)] bg-[rgb(var(--app-surface)/0.95)] px-3 py-1.5 text-[11px] font-medium text-[rgb(var(--app-brand-strong))] shadow-sm backdrop-blur">
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[rgb(var(--app-brand)/0.18)] border-t-[rgb(var(--app-brand-strong))]" />
            <span>{refreshLabel}</span>
          </div>
        </div>
      )}
      <div className="max-w-full overflow-x-auto">
        <div style={style}>{children}</div>
      </div>
    </div>
  );
};

const TableHeader: React.FC<TableHeaderProps> = ({ children, className }) => {
  return <thead className={cx(tableHeaderBase, className)}>{children}</thead>;
};

const TableBody: React.FC<TableBodyProps> = ({ children, className }) => {
  return <tbody className={cx(tableBodyBase, className)}>{children}</tbody>;
};

const TableRow: React.FC<TableRowProps> = ({
  children,
  className,
  onClick,
  interactive = false,
  striped = false,
}) => {
  return (
    <tr
      className={cx(
        'transition-colors',
        striped && 'bg-[rgb(var(--app-surface-muted)/0.55)]',
        interactive &&
          'cursor-pointer hover:bg-[rgb(var(--app-surface-strong)/0.72)]',
        className,
      )}
      onClick={onClick}
    >
      {children}
    </tr>
  );
};

const TableCell: React.FC<TableCellProps> = ({
  children,
  isHeader = false,
  className,
  colSpan,
  align = 'left',
}) => {
  const CellTag = isHeader ? 'th' : 'td';
  return (
    <CellTag
      className={cx(
        isHeader ? tableHeaderCellBase : tableBodyCellBase,
        cellAlignment[align],
        className,
      )}
      colSpan={colSpan}
    >
      {children}
    </CellTag>
  );
};

const TableState: React.FC<TableStateProps> = ({ children }) => (
  <div className="flex min-h-[220px] items-center justify-center px-6 py-12">
    {children}
  </div>
);

const TableLoadingState: React.FC<TableLoadingStateProps> = ({
  label = 'Loading data…',
}) => (
  <TableState>
    <div className="flex items-center gap-3 text-sm font-medium text-[rgb(var(--app-muted))]">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-[rgb(var(--app-border)/0.7)] border-t-[rgb(var(--app-brand-strong))]" />
      <span>{label}</span>
    </div>
  </TableState>
);

const TableErrorState: React.FC<TableErrorStateProps> = ({ message }) => (
  <TableState>
    <div className="max-w-md rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600 dark:border-error-800/70 dark:bg-error-900/20 dark:text-error-300">
      {message}
    </div>
  </TableState>
);

const TableSortButton: React.FC<TableSortButtonProps> = ({
  label,
  isActive,
  direction,
  onClick,
  className,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      'inline-flex items-center gap-1.5 font-semibold tracking-normal text-inherit transition-colors hover:text-[rgb(var(--app-text))]',
      className,
    )}
  >
    <span>{label}</span>
    <SortArrow active={isActive} direction={direction} />
  </button>
);

const TableBadge: React.FC<TableBadgeProps> = ({
  children,
  tone = 'neutral',
}) => (
  <Badge variant="light" size="sm" color={tableBadgeColorMap[tone]}>
    {children}
  </Badge>
);

export {
  Table,
  TableShell,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableState,
  TableLoadingState,
  TableErrorState,
  TableSortButton,
  TableBadge,
};
